import { useState, useCallback, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { useAccountStore } from '../../stores/accountStore';
import { ExchangeOrderBook } from '@/components/organisms/ExchangeOrderBook';
import { useHydraOrderbookWarmup } from '@/hooks/useHydraOrderbookWarmup';
import { useFeeFor } from '@/services/hydraFees';
import { perContractFeeCents } from '@/lib/pricing/fees';
import type { GameRow, TeamSide, MarketSide, BookLevel, VenueBook } from './types';
import { americanOdds, bestPriceCentsForOrderSide, outcomeSideLabel } from './tradeModalBook';

// Same phantom-level filter the inline ladder + Yes/No pills use, hoisted
// here so the right-side deep orderbook shows the same sane view rather
// than diverging on tiny stub orders / cross-spread ghosts.
const LEVEL_SANITY_SLACK = 15;
function hasSize(l: BookLevel): boolean {
  return l.size >= 1;
}
function bidIsReal(lastTradeCents: number | null | undefined) {
  return (l: BookLevel): boolean => {
    if (!hasSize(l)) return false;
    if (lastTradeCents == null) return true;
    return l.priceCents <= lastTradeCents + LEVEL_SANITY_SLACK;
  };
}
/** Real best-bid price for the requested side (YES or NO). Reads the actual
 *  side ladder via bestPriceCentsForOrderSide rather than inverting YES — so
 *  this strip matches the orderbook the user is staring at, even when
 *  YES + NO don't sum to 100¢ across a wide spread. */
function formatSidePriceCents(book: VenueBook | undefined | null, side: MarketSide): string {
  const cents = bestPriceCentsForOrderSide(book ?? undefined, side);
  return cents == null ? '—' : `${cents}¢`;
}

function buildSide(
  book: VenueBook | null | undefined,
  marketSide: MarketSide,
): { bids: BookLevel[]; asks: BookLevel[] } {
  if (!book) return { bids: [], asks: [] };
  const ownBids = marketSide === 'yes' ? book.yes : book.no;
  const oppositeBids = marketSide === 'yes' ? book.no : book.yes;
  const lastYes = book.lastPriceCents;
  const ownLast = lastYes == null ? null : marketSide === 'yes' ? lastYes : 100 - lastYes;
  const oppositeLast = lastYes == null ? null : marketSide === 'yes' ? 100 - lastYes : lastYes;
  return {
    bids: ownBids.filter(bidIsReal(ownLast)),
    asks: oppositeBids
      .filter(bidIsReal(oppositeLast))
      .map((l) => ({ priceCents: 100 - l.priceCents, size: l.size })),
  };
}

export interface TradeIntent {
  side: 'away' | 'home';
  venue: 'kalshi' | 'polymarket';
}

interface GameDetailPanelProps {
  game: GameRow;
  onClose: () => void;
  tradeIntent?: TradeIntent | null;
  marketSide: MarketSide;
  /** Keeps parent trade intent in sync when the user switches team tabs (row-click toggle uses this). */
  onTeamSideChange?: (side: TeamSide) => void;
}

// ── Order Entry Form ────────────────────────────────────────────

function OrderEntryForm({
  game,
  selectedSide,
  orderSide,
  onOrderSideChange,
  price,
  onPriceChange,
  orderType,
  onOrderTypeChange,
}: {
  game: GameRow;
  selectedSide: TeamSide;
  /** Controlled by GameDetailPanel — same value drives the orderbook ladders
   *  below, so toggling here ripples down but never up to the moneylines
   *  table (which holds its own independent marketSide). */
  orderSide: MarketSide;
  onOrderSideChange: (next: MarketSide) => void;
  /** Controlled price string — clicking a bid level in the orderbook
   *  below populates this; market orders ignore it. */
  price: string;
  onPriceChange: (next: string) => void;
  /** 'limit' (post a resting bid at `price`) vs 'market' (take whatever's
   *  on the ask). Clicking an ask level flips this to 'market'. */
  orderType: 'limit' | 'market';
  onOrderTypeChange: (next: 'limit' | 'market') => void;
}) {
  const status = useAccountStore((s) => s.status);
  const placeOrder = useAccountStore((s) => s.placeOrder);

  const [qty, setQty] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  // Get the Kalshi ticker for the selected team
  const kalshiBook = selectedSide === 'away' ? game.kalshi?.away : game.kalshi?.home;
  const ticker = kalshiBook?.marketTicker ?? '';

  const bestYesHint = bestPriceCentsForOrderSide(kalshiBook, 'yes');
  const bestNoHint = bestPriceCentsForOrderSide(kalshiBook, 'no');

  const applyOrderSide = useCallback(
    (next: MarketSide) => {
      onOrderSideChange(next);
      setResult(null);
      const hint = bestPriceCentsForOrderSide(kalshiBook, next);
      if (hint !== undefined) onPriceChange(String(hint));
    },
    [kalshiBook, onOrderSideChange, onPriceChange],
  );

  // When the panel's side flips (main moneylines toggle pushed in, or the
  // user clicked our own BUY YES/NO above), refresh the price hint.
  useEffect(() => {
    setResult(null);
    const hint = bestPriceCentsForOrderSide(kalshiBook, orderSide);
    if (hint !== undefined) onPriceChange(String(hint));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSide]);

  useEffect(() => {
    const hint = bestPriceCentsForOrderSide(kalshiBook, orderSide);
    if (hint === undefined) return;
    if (price === '') onPriceChange(String(hint));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kalshiBook, orderSide]);

  const handleSubmit = useCallback(async () => {
    const priceCents = parseInt(price, 10);
    const count = parseInt(qty, 10);

    if (!ticker) { setResult({ ok: false, msg: 'No Kalshi market found' }); return; }
    if (isNaN(count) || count < 1) { setResult({ ok: false, msg: 'Qty must be at least 1' }); return; }
    if (orderType === 'limit') {
      if (isNaN(priceCents) || priceCents < 1 || priceCents > 99) {
        setResult({ ok: false, msg: 'Price must be 1-99¢' });
        return;
      }
    }

    setSubmitting(true);
    setResult(null);
    try {
      await placeOrder({
        ticker,
        side: orderSide,
        action: 'buy',
        type: orderType,
        count,
        ...(orderType === 'limit' ? { price: priceCents } : {}),
      });
      setResult({
        ok: true,
        msg:
          orderType === 'limit'
            ? `Order placed: ${count} ${orderSide.toUpperCase()} @ ${priceCents}¢`
            : `Market order placed: ${count} ${orderSide.toUpperCase()}`,
      });
      setQty('');
    } catch (err) {
      setResult({ ok: false, msg: err instanceof Error ? err.message : 'Order failed' });
    } finally {
      setSubmitting(false);
    }
  }, [ticker, orderSide, orderType, price, qty, placeOrder]);

  if (status !== 'connected') {
    return (
      <div className="border-border border-b px-4 py-3">
        <p className="text-muted-foreground text-xs">Connect your Kalshi account in Settings to trade</p>
      </div>
    );
  }

  if (!ticker) return null;

  const priceCents = parseInt(price, 10) || 0;
  const count = parseInt(qty, 10) || 0;
  const cost = (priceCents * count) / 100;

  // Hydra-sourced per-series fee schedule. `feeEntry` is undefined while the
  // initial fetch is in flight (or for a series the schedule hasn't seen yet);
  // when it is, we render "—" instead of guessing a rate.
  const feeEntry = useFeeFor(ticker);
  // Limit orders rest = maker; market orders cross = taker. This is a
  // pre-fill *estimate*: a limit that never fills costs nothing, and a
  // marketable limit can be matched as a taker. See PROJECT-BIBLE.md.
  const isMaker = orderType === 'limit';
  const feeRate = !feeEntry
    ? null
    : feeEntry.feeType === 'flat'
      ? null
      : isMaker
        ? (feeEntry.feeMakerRate ?? 0)
        : (feeEntry.feeTakerRate ?? 0);
  const feePerContractCents =
    feeRate === null || priceCents === 0 ? null : perContractFeeCents(priceCents, feeRate);
  const fee =
    feePerContractCents === null || count === 0
      ? null
      : (feePerContractCents / 100) * count;
  const feeLabel = feeRate === null ? '—' : `$${(fee ?? 0).toFixed(3)}`;
  // Effective price = quoted price + per-contract fee (cents). Drives American
  // odds of the contract *after* fee, which is what the trader actually pays.
  const effectivePriceCents =
    feePerContractCents === null ? null : priceCents + feePerContractCents;
  const netPayout = fee === null ? null : count - fee;
  const netPayoutLabel = netPayout === null ? '—' : `$${netPayout.toFixed(2)}`;
  const effectiveOddsLabel =
    effectivePriceCents === null ? '—' : americanOdds(effectivePriceCents);

  return (
    <div className="border-border space-y-3 border-b px-4 py-3">
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground text-xs font-medium">Order Entry</span>
        <span className="text-muted-foreground/50 font-mono text-[10px]">{ticker.length > 30 ? `...${ticker.slice(-28)}` : ticker}</span>
      </div>

      {/* Side toggle */}
      <div className="space-y-1.5">
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => applyOrderSide('yes')}
            className={cn(
              'flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors',
              orderSide === 'yes'
                ? 'bg-positive text-white'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            )}
          >
            BUY YES
          </button>
          <button
            type="button"
            onClick={() => applyOrderSide('no')}
            className={cn(
              'flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors',
              orderSide === 'no'
                ? 'bg-negative text-white'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            )}
          >
            BUY NO
          </button>
        </div>
        <p className="text-muted-foreground text-center text-[11px]">
          {outcomeSideLabel(game, selectedSide, orderSide)}
        </p>
      </div>

      {/* Limit / Market toggle — clicking a bid level in the orderbook below
          flips this to LIMIT, clicking an ask flips it to MARKET. User can
          also toggle manually. */}
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={() => { onOrderTypeChange('limit'); setResult(null); }}
          className={cn(
            'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
            orderType === 'limit'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:text-foreground',
          )}
        >
          LIMIT
        </button>
        <button
          type="button"
          onClick={() => { onOrderTypeChange('market'); setResult(null); }}
          className={cn(
            'flex-1 rounded-md py-1 text-[11px] font-semibold transition-colors',
            orderType === 'market'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:text-foreground',
          )}
        >
          MARKET
        </button>
      </div>

      {/* Price + Qty */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-muted-foreground mb-1 block text-[10px]">
            Price (¢){orderType === 'market' ? ' — n/a' : ''}
          </label>
          <input
            type="number"
            min={1}
            max={99}
            disabled={orderType === 'market'}
            value={orderType === 'market' ? '' : price}
            onChange={(e) => { onPriceChange(e.target.value); setResult(null); }}
            placeholder={
              orderType === 'market'
                ? 'best ask'
                : (orderSide === 'yes' ? bestYesHint : bestNoHint) !== undefined
                  ? String(orderSide === 'yes' ? bestYesHint : bestNoHint)
                  : '50'
            }
            className="border-border bg-input text-foreground placeholder-muted-foreground focus:border-primary w-full rounded-md border px-2.5 py-1.5 font-mono text-xs focus:outline-none disabled:opacity-50"
          />
        </div>
        <div>
          <label className="text-muted-foreground mb-1 block text-[10px]">Qty</label>
          <input
            type="number"
            min={1}
            value={qty}
            onChange={(e) => { setQty(e.target.value); setResult(null); }}
            placeholder="0"
            className="border-border bg-input text-foreground placeholder-muted-foreground focus:border-primary w-full rounded-md border px-2.5 py-1.5 font-mono text-xs focus:outline-none"
          />
        </div>
      </div>

      {/* Submit — sits directly under the price/qty inputs so the primary
          action is always reachable without scrolling past the summary. */}
      <button
        onClick={handleSubmit}
        disabled={submitting || !price || !qty}
        className="bg-primary text-primary-foreground hover:bg-primary/90 flex w-full items-center justify-center gap-2 rounded-md py-2 text-xs font-semibold transition-colors disabled:opacity-50"
      >
        {submitting && <Loader2 size={12} className="animate-spin" />}
        {submitting ? 'Placing...' : 'Place Order'}
      </button>

      {/* Price / Cost(Fee) / Payout — fee is the per-series Kalshi rate from
          hydra (taker on market, maker on limit). Payout is qty in dollars
          (each contract pays $1 on win). Maker fees only apply on fill — a
          resting limit that never fills costs nothing. */}
      {count > 0 && priceCents > 0 && (
        <div className="space-y-1 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Price</span>
            <span className="text-foreground font-mono inline-flex items-center gap-1.5">
              <span>{priceCents}¢</span>
              <span className="h-3 w-px self-center bg-current opacity-30" />
              <span className="opacity-70">{americanOdds(priceCents)}</span>
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span
              className="text-muted-foreground"
              title={
                isMaker
                  ? 'Maker fee — applied only when the resting order fills'
                  : 'Taker fee — applied immediately on fill'
              }
            >
              Cost (fee)
            </span>
            <span className="text-foreground font-mono">
              ${cost.toFixed(2)}{' '}
              <span className="text-muted-foreground">({feeLabel})</span>
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground" title="Payout minus fee">
              Payout
            </span>
            <span className="text-positive font-mono inline-flex items-center gap-1.5">
              <span>{netPayoutLabel}</span>
              <span className="h-3 w-px self-center bg-current opacity-30" />
              <span className="opacity-70">{effectiveOddsLabel}</span>
            </span>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className={cn(
          'rounded-md px-2.5 py-1.5 text-xs',
          result.ok ? 'bg-positive/10 text-positive' : 'bg-negative/10 text-negative'
        )}>
          {result.msg}
        </div>
      )}
    </div>
  );
}

// ── Game Detail Panel ───────────────────────────────────────────

export function GameDetailPanel({ game, onClose, tradeIntent, marketSide, onTeamSideChange }: GameDetailPanelProps) {
  const [selectedSide, setSelectedSide] = useState<TeamSide>(tradeIntent?.side ?? 'away');

  // Panel-local YES/NO state. Initialized from `marketSide` and re-synced
  // whenever the parent changes it (so toggling YES/NO on the moneylines
  // table still cascades into the panel + its ladders). The order-entry
  // form's BUY YES/BUY NO toggle drives this back through onOrderSideChange,
  // which only updates this local state — the parent's marketSide is never
  // pushed up, so the moneylines table stays put.
  const [panelSide, setPanelSide] = useState<MarketSide>(marketSide);
  useEffect(() => {
    setPanelSide(marketSide);
  }, [marketSide]);

  // Form intent state lifted up so orderbook-row clicks can populate the
  // OrderEntryForm: clicking a bid prefills the price + flips type=limit;
  // clicking an ask flips type=market (price ignored).
  const [formPrice, setFormPrice] = useState('');
  const [formOrderType, setFormOrderType] = useState<'limit' | 'market'>('limit');

  useEffect(() => {
    setSelectedSide(tradeIntent?.side ?? 'away');
  }, [game.key, tradeIntent]);

  // Ask Hydra to subscribe upstream to this game's Kalshi orderbooks while the
  // panel is open. No-op until Hydra serves /api/* over HTTPS — see
  // docs/hydra-server-asks.md.
  useHydraOrderbookWarmup([game.kalshi?.away.marketTicker, game.kalshi?.home.marketTicker]);

  const teamName = selectedSide === 'away' ? game.awayName : game.homeName;
  const kalshiBook = selectedSide === 'away' ? game.kalshi?.away : game.kalshi?.home;
  const polyBook = selectedSide === 'away' ? game.polymarket?.away : game.polymarket?.home;

  // Drive bids/asks off panelSide, with the same phantom-level sanity filter
  // the inline ladder + Yes/No pills use so this panel matches them at the
  // spread.
  //   YES side: bids = book.yes (yes bids), asks = 100 − no bids
  //   NO  side: bids = book.no  (no bids),  asks = 100 − yes bids
  const kalshiSide = buildSide(kalshiBook, panelSide);
  const polySide = buildSide(polyBook, panelSide);

  return (
    <div className="bg-card/95 border-border flex h-full w-96 flex-col border-l">
      {/* Header */}
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <div>
          <h3 className="text-foreground text-sm font-semibold">
            {game.awayName} @ {game.homeName}
          </h3>
          <p className="text-muted-foreground mt-0.5 text-xs">
            {game.date} &middot; {game.timePt} PT
          </p>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:bg-accent hover:text-foreground rounded p-1">
          <X size={16} />
        </button>
      </div>

      {/* Team selector */}
      <div className="border-border flex border-b">
        <button
          className={cn(
            'flex-1 py-2 text-center text-sm font-medium transition-colors',
            selectedSide === 'away'
              ? 'bg-primary/20 text-primary border-primary border-b-2'
              : 'text-muted-foreground hover:text-foreground'
          )}
          onClick={() => {
            setSelectedSide('away');
            onTeamSideChange?.('away');
          }}
        >
          {game.awayName}
        </button>
        <button
          className={cn(
            'flex-1 py-2 text-center text-sm font-medium transition-colors',
            selectedSide === 'home'
              ? 'bg-primary/20 text-primary border-primary border-b-2'
              : 'text-muted-foreground hover:text-foreground'
          )}
          onClick={() => {
            setSelectedSide('home');
            onTeamSideChange?.('home');
          }}
        >
          {game.homeName}
        </button>
      </div>

      {/* Order Entry */}
      <OrderEntryForm
        game={game}
        selectedSide={selectedSide}
        orderSide={panelSide}
        onOrderSideChange={setPanelSide}
        price={formPrice}
        onPriceChange={setFormPrice}
        orderType={formOrderType}
        onOrderTypeChange={setFormOrderType}
      />

      {/* Price summary \u2014 book.priceCents is the YES price; flip to (100 \u2212 yes)
          when the panel is showing NO so this strip tracks the side toggle. */}
      <div className="border-border border-b px-4 py-2">
        <div className="grid grid-cols-3 gap-2">
          <div />
          <div className="text-muted-foreground text-center text-[10px] font-semibold uppercase">{game.awayCode}</div>
          <div className="text-muted-foreground text-center text-[10px] font-semibold uppercase">{game.homeCode}</div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="text-xs font-semibold text-blue-400">K</div>
          <div className="text-foreground/80 text-center font-mono text-xs">
            {formatSidePriceCents(game.kalshi?.away, panelSide)}
          </div>
          <div className="text-foreground/80 text-center font-mono text-xs">
            {formatSidePriceCents(game.kalshi?.home, panelSide)}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="text-xs font-semibold text-purple-400">P</div>
          <div className="text-foreground/80 text-center font-mono text-xs">
            {formatSidePriceCents(game.polymarket?.away, panelSide)}
          </div>
          <div className="text-foreground/80 text-center font-mono text-xs">
            {formatSidePriceCents(game.polymarket?.home, panelSide)}
          </div>
        </div>
      </div>

      {/* Orderbooks */}
      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        {kalshiBook && (
          <ExchangeOrderBook
            venue="kalshi"
            teamLabel={`${teamName} — ${panelSide === 'yes' ? 'YES' : 'NO'}`}
            bids={kalshiSide.bids}
            asks={kalshiSide.asks}
            onLevelClick={(info) => {
              // Bid click → resting limit at that price.
              // Ask click → take-the-offer market order.
              if (info.side === 'bid') {
                setFormOrderType('limit');
                setFormPrice(String(info.priceCents));
              } else {
                setFormOrderType('market');
              }
            }}
          />
        )}
        {polyBook && (
          <ExchangeOrderBook
            venue="polymarket"
            teamLabel={`${teamName} — ${panelSide === 'yes' ? 'YES' : 'NO'}`}
            bids={polySide.bids}
            asks={polySide.asks}
          />
        )}
      </div>
    </div>
  );
}
